import React, { useCallback, useRef, useState } from 'react'
import { F0Box, F0Button, F0TagPerson, F0TagStatus, F0TagTeam, F0Text } from '@factorialco/f0-react'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  RichTextDisplay,
  RichTextEditor,
  RichTextEditorHandle,
  Select,
  Stack,
} from '@factorialco/f0-react/dist/experimental'
import i18n from 'modules/core/lib/i18n'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import TicketPriority from 'modules/ticketing/components/TicketField/TicketPriority'
import TicketStatus from 'modules/ticketing/components/TicketField/TicketStatus'
import { AssigneeSelect } from 'modules/ticketing/components/AssigneeSelect/AssigneeSelect'
import { ResolverTeamSelector } from 'modules/ticketing/components/ResolverTeamSelector'
import type { TicketDetailQuery } from 'generated/resources/graphql'
import { formatTimeRemaining, isOverdue } from 'modules/ticketing/lib/ticketTime'
import { getTicketStatusLabel, getTicketStatusOptions } from 'modules/ticketing/lib/ticketStatus'
import {
  getTicketPriorityLabel,
  getTicketPriorityOptions,
} from 'modules/ticketing/lib/ticketPriority'

export type TicketDetailListProps = {
  ticket: TicketDetailQuery['ticketing']['ticket']
  onStatusChange?: (newStatus: string) => void
  onPriorityChange?: (newPriority: string) => void
  onAssigneeChange?: (fields: { assigneeTeamId?: string; assigneeMemberId?: string | null }) => void
  onDescriptionChange?: (newDescription: string) => boolean | Promise<boolean>
}

const REQUESTER_STATUS_OPTIONS = ['open', 'resolved']

const DescriptionField: React.FC<{
  description: string | null | undefined
  canEdit: boolean
  onSave?: (newDescription: string) => boolean | Promise<boolean>
}> = ({ description, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false)
  const [editedContent, setEditedContent] = useState(description || '')
  const editorRef = useRef<RichTextEditorHandle>(null)
  const { editorConfig, loading: loadingEditor } = useRichTextEditor()

  const handleSave = useCallback(async () => {
    const success = await Promise.resolve(onSave?.(editedContent))
    if (success !== false) setEditing(false)
  }, [onSave, editedContent])

  const handleCancel = useCallback(() => {
    setEditing(false)
    setEditedContent(description || '')
  }, [description])

  const handleStartEdit = useCallback(() => {
    setEditedContent(description || '')
    setEditing(true)
  }, [description])

  if (editing) {
    return (
      <Stack className="col-span-2" gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.one_ticket_card.description')} />
        {loadingEditor ? (
          <RichTextEditor.Skeleton rows={4} />
        ) : (
          <RichTextEditor
            ref={editorRef}
            {...editorConfig}
            title={i18n.t('ticketing.inbox.one_ticket_card.description')}
            placeholder={i18n.t('ticketing.inbox.one_ticket_card.description')}
            initialEditorState={{ content: description || '' }}
            onChange={(result) => setEditedContent(result?.value ?? '')}
          />
        )}
        <F0Box display="flex" gap="md" justifyContent="end">
          <F0Button
            size="md"
            variant="outline"
            label={i18n.t('ticketing.inbox.detail.cancel_edit')}
            onClick={handleCancel}
          />
          <F0Button
            size="md"
            variant="default"
            label={i18n.t('ticketing.inbox.detail.save_description')}
            onClick={handleSave}
          />
        </F0Box>
      </Stack>
    )
  }

  return (
    <Stack className="col-span-2" gap="2">
      <F0Box display="flex" gap="sm" alignItems="center">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.one_ticket_card.description')} />
        {canEdit && onSave && (
          <F0Button
            size="sm"
            variant="ghost"
            icon={Pencil}
            hideLabel
            label={i18n.t('ticketing.inbox.detail.edit_description')}
            onClick={handleStartEdit}
          />
        )}
      </F0Box>
      {description ? (
        <RichTextDisplay format="html" content={description} />
      ) : (
        <F0Text variant="body" content="—" />
      )}
    </Stack>
  )
}

export const TicketDetailList: React.FC<TicketDetailListProps> = ({
  ticket,
  onStatusChange,
  onPriorityChange,
  onAssigneeChange,
  onDescriptionChange,
}) => {
  if (!ticket) return null

  const requesterDisplayName = ticket.requesterName ?? `#${ticket.requesterId}`

  const statusOptions = ticket.canEdit
    ? getTicketStatusOptions()
    : Array.from(new Set([ticket.status, ...REQUESTER_STATUS_OPTIONS]))

  return (
    <Stack className="grid grid-cols-2 gap-6 pt-4">
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.status')} />
        {ticket.canEditStatus && onStatusChange ? (
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.status')}
            value={ticket.status}
            onChange={(value: string) => onStatusChange(value)}
            options={statusOptions.map((s) => ({
              value: s,
              label: getTicketStatusLabel(s),
            }))}
            hideLabel
          />
        ) : (
          <TicketStatus status={ticket.status} />
        )}
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.priority')} />
        {ticket.canEdit && onPriorityChange ? (
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.priority')}
            value={ticket.priority}
            onChange={(value: string) => onPriorityChange(value)}
            options={getTicketPriorityOptions().map((p) => ({
              value: p,
              label: getTicketPriorityLabel(p),
            }))}
            hideLabel
          />
        ) : (
          <TicketPriority priority={ticket.priority} />
        )}
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.assignee_team')} />
        {ticket.canEdit && onAssigneeChange ? (
          <ResolverTeamSelector
            hideLabel
            value={
              ticket.assigneeTeamId != null
                ? { id: String(ticket.assigneeTeamId), name: ticket.assigneeTeamName ?? '' }
                : null
            }
            onSelect={(team) => {
              onAssigneeChange({ assigneeTeamId: team?.id, assigneeMemberId: null })
            }}
          />
        ) : ticket.assigneeTeamName ? (
          <F0TagTeam name={ticket.assigneeTeamName} src={ticket.assigneeTeamName} />
        ) : (
          <F0TagStatus
            text={i18n.t('ticketing.inbox.assignee_team_unassigned')}
            variant="neutral"
          />
        )}
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.assignee_member')} />
        {ticket.canEdit && onAssigneeChange ? (
          <AssigneeSelect
            label={''}
            defaultValue={ticket.assigneeMemberId != null ? String(ticket.assigneeMemberId) : ''}
            teamId={ticket.assigneeTeamId ?? undefined}
            onChange={(item) => {
              onAssigneeChange({
                assigneeTeamId: ticket.assigneeTeamId ?? undefined,
                assigneeMemberId:
                  item === null || !item.subItems?.[0]?.subId
                    ? null
                    : String(item.subItems[0].subId),
              })
            }}
            disabled={ticket.assigneeTeamId == null || ticket.assigneeTeamId === '0'}
          />
        ) : ticket.assigneeMemberName ? (
          <F0TagPerson name={ticket.assigneeMemberName} />
        ) : (
          <F0TagStatus
            text={i18n.t('ticketing.inbox.assignee_member_unassigned')}
            variant="neutral"
          />
        )}
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.requested_by')} />
        <F0TagPerson name={requesterDisplayName} />
      </Stack>
      {ticket.slaDueAt && (
        <Stack gap="2">
          <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.time_remaining')} />
          {isOverdue(ticket.timeRemainingToSlaSeconds) ? (
            <F0TagStatus
              text={formatTimeRemaining(ticket.timeRemainingToSlaSeconds)}
              variant="critical"
            />
          ) : (
            <F0Text content={formatTimeRemaining(ticket.timeRemainingToSlaSeconds)} />
          )}
        </Stack>
      )}
      {(ticket.description || ticket.canEditDescription) && (
        <DescriptionField
          description={ticket.description}
          canEdit={ticket.canEditDescription}
          onSave={onDescriptionChange}
        />
      )}
    </Stack>
  )
}
